/**
 * NOTE: This code belongs to Kangaroo Rewards. Unauthorized use is prohibited
 */

@file:Suppress("unused", "MemberVisibilityCanBePrivate", "EXPERIMENTAL_API_USAGE")
@file:OptIn(ExperimentalJsExport::class)
package features.publicAmazonCatalogue

import kangaroorewards.appsdk.core.api.Api
import kangaroorewards.appsdk.core.domain.Result
import kangaroorewards.appsdk.core.domain.Result.Idle
import kangaroorewards.appsdk.core.domain.Result.Loading
import kangaroorewards.appsdk.core.domain.SerializedResult
import kangaroorewards.appsdk.core.domain.toJsonResult
import kangaroorewards.appsdk.core.utils.CFlow
import kangaroorewards.appsdk.core.utils.asCFlow
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.serialization.InternalSerializationApi
import kotlin.js.ExperimentalJsExport
import features.publicAmazonCatalogue.internal.PublicAmazonCatalogueUseCase
import features.publicAmazonCatalogue.internal.PublicAmazonCatalogueUseCase.Params
import features.publicAmazonCatalogue.models.PublicRewardsModel


typealias PublicAmazonCatalogueState = CFlow<PublicAmazonCatalogueUseCaseResult?>

typealias PublicAmazonCatalogueUseCaseResult = Result<PublicRewardsModel>

fun PublicAmazonCatalogueUseCaseResult.serializePublicAmazonCatalogueApiResult(): SerializedResult<String> {
    return this.toJsonResult<PublicRewardsModel>()
}

/**
 * Serializes [PublicAmazonCatalogueState]'s data and returns a new
 * [CFlow] containing data serialized as a JSON [String].
 */
@OptIn(ExperimentalCoroutinesApi::class)
fun PublicAmazonCatalogueState.serializePublicAmazonCatalogueState(): CFlow<SerializedResult<String>?> {
    return this.mapLatest {
        it?.toJsonResult<PublicRewardsModel>()
    }.asCFlow()
}

/**
 * Get a list of rewards available to all users including the amazon catalogue. [Api] class
 */
class PublicAmazonCatalogueApi : Api() {

    val publicAmazonCatalogueState: PublicAmazonCatalogueState
        get() = _publicAmazonCatalogueState.asCFlow()

    companion object {
        private val _publicAmazonCatalogueState: MutableStateFlow<PublicAmazonCatalogueUseCaseResult> = MutableStateFlow(Idle())
    }

    private val publicAmazonCatalogueUseCase = PublicAmazonCatalogueUseCase()

    /**
     * Get a list of rewards available to all users including the amazon catalogue.
     */
    suspend fun getPublicAmazonCatalogue(
        overrideHeaders: Map<String, String>? = null,
        pageNumber: Int,
        perPage: Int,
        keywords: String?,
        filters: String?
    ): Result<PublicRewardsModel> {
        /* set state to Loading as soon as function is called */
        _publicAmazonCatalogueState.value = Loading()
        val result = this.publicAmazonCatalogueUseCase(
            Params(
                overrideHeaders = overrideHeaders,
                pageNumber = pageNumber,
                perPage = perPage,
                keywords = keywords,
                filters = filters
            )
        ) { result ->
            _publicAmazonCatalogueState.value = result
            return@publicAmazonCatalogueUseCase result
        }
        return result
    }

}
